import { useCallback } from 'react';
import { useRequiredParamsSetter } from '../context/TargetingSettingsContext.tsx';
import {
  type RequiredParamsType,
  RequiredTargetingSectionKeys,
  TargetingElementKeys,
  type TargetingElementKeysType,
} from '../context/types.ts';
import { Operation } from '../hooks/types.ts';
import { useTargetingSelectionActions } from './useTargetingSelectionActions.tsx';

export const useFormatOperations = () => {
  const set = useRequiredParamsSetter();
  const { updateSectionElements } = useTargetingSelectionActions();

  const handleBannerOrNativeSelect = useCallback(
    (sections: RequiredParamsType['sections'], elementKey: TargetingElementKeysType) => {
      updateSectionElements(sections, RequiredTargetingSectionKeys.FORMAT, [
        { type: Operation.BLOCK_SECTION, elementKey },
      ]);

      updateSectionElements(sections, RequiredTargetingSectionKeys.CHANNEL, [
        { type: Operation.SELECT, elementKey: TargetingElementKeys.CHANNEL_APP },
        { type: Operation.SELECT, elementKey: TargetingElementKeys.CHANNEL_WEB },
      ]);

      updateSectionElements(sections, RequiredTargetingSectionKeys.DEVICE, [
        { type: Operation.SELECT, elementKey: TargetingElementKeys.DEVICE_DESKTOP },
        { type: Operation.SELECT, elementKey: TargetingElementKeys.DEVICE_MOBILE },
        { type: Operation.BLOCK, elementKey: TargetingElementKeys.DEVICE_TV },
      ]);
    },
    [updateSectionElements]
  );

  const handleVideoSelect = useCallback(
    (sections: RequiredParamsType['sections'], elementKey: TargetingElementKeysType) => {
      updateSectionElements(sections, RequiredTargetingSectionKeys.FORMAT, [
        { type: Operation.BLOCK_SECTION, elementKey },
      ]);

      updateSectionElements(sections, RequiredTargetingSectionKeys.CHANNEL, [
        { type: Operation.SELECT, elementKey: TargetingElementKeys.CHANNEL_APP },
        { type: Operation.SELECT, elementKey: TargetingElementKeys.CHANNEL_WEB },
      ]);

      updateSectionElements(sections, RequiredTargetingSectionKeys.DEVICE, [
        { type: Operation.SELECT, elementKey: TargetingElementKeys.DEVICE_DESKTOP },
        { type: Operation.SELECT, elementKey: TargetingElementKeys.DEVICE_MOBILE },
        { type: Operation.ENABLE, elementKey: TargetingElementKeys.DEVICE_TV },
        { type: Operation.DESELECT, elementKey: TargetingElementKeys.DEVICE_TV },
      ]);
    },
    [updateSectionElements]
  );

  const handleCTVSelect = useCallback(
    (sections: RequiredParamsType['sections']) => {
      updateSectionElements(sections, RequiredTargetingSectionKeys.FORMAT, [
        { type: Operation.BLOCK_SECTION, elementKey: TargetingElementKeys.FORMAT_CTV },
      ]);

      updateSectionElements(sections, RequiredTargetingSectionKeys.CHANNEL, [
        { type: Operation.SELECT, elementKey: TargetingElementKeys.CHANNEL_APP },
        { type: Operation.BLOCK, elementKey: TargetingElementKeys.CHANNEL_WEB },
      ]);

      updateSectionElements(sections, RequiredTargetingSectionKeys.DEVICE, [
        { type: Operation.SELECT, elementKey: TargetingElementKeys.DEVICE_TV },
        { type: Operation.BLOCK, elementKey: TargetingElementKeys.DEVICE_DESKTOP },
        { type: Operation.BLOCK, elementKey: TargetingElementKeys.DEVICE_MOBILE },
      ]);
    },
    [updateSectionElements]
  );

  const handleDeselect = useCallback(
    (sections: RequiredParamsType['sections'], elementIndex: number) => {
      const elementKey = sections[RequiredTargetingSectionKeys.FORMAT].elements[elementIndex].mainOption
        .key as TargetingElementKeysType;

      updateSectionElements(sections, RequiredTargetingSectionKeys.FORMAT, [
        { type: Operation.ENABLE_SECTION, elementKey: elementKey },
      ]);

      [RequiredTargetingSectionKeys.CHANNEL, RequiredTargetingSectionKeys.DEVICE].forEach(sectionKey => {
        updateSectionElements(sections, sectionKey, [{ type: Operation.BLOCK_SECTION, elementKey: null }]);
      });
    },
    [updateSectionElements]
  );

  const onCTVClick = useCallback(
    (_event, sectionKey, elementIndex) => {
      set((prev: RequiredParamsType) => {
        const isElementSelected = prev.sections[sectionKey].elements[elementIndex].mainOption.isSelected;

        if (!isElementSelected) {
          handleCTVSelect(prev.sections);
        } else {
          handleDeselect(prev.sections, elementIndex);
        }

        return { ...prev };
      });
    },
    [set, handleDeselect, handleCTVSelect]
  );

  const onBannerOrNativeClick = useCallback(
    (_event, sectionKey, elementIndex) => {
      set((prev: RequiredParamsType) => {
        const isElementSelected = prev.sections[sectionKey].elements[elementIndex].mainOption.isSelected;
        const elementKey = prev.sections[sectionKey].elements[elementIndex].mainOption.key;

        if (!isElementSelected) {
          handleBannerOrNativeSelect(prev.sections, elementKey);
        } else {
          handleDeselect(prev.sections, elementIndex);
        }

        return { ...prev };
      });
    },
    [set, handleDeselect, handleBannerOrNativeSelect]
  );

  const onVideoClick = useCallback(
    (_event, sectionKey, elementIndex) => {
      set((prev: RequiredParamsType) => {
        const isElementSelected = prev.sections[sectionKey].elements[elementIndex].mainOption.isSelected;
        const elementKey = prev.sections[sectionKey].elements[elementIndex].mainOption.key;

        if (!isElementSelected) {
          handleVideoSelect(prev.sections, elementKey);
        } else {
          handleDeselect(prev.sections, elementIndex);
        }

        return { ...prev };
      });
    },
    [set, handleDeselect, handleVideoSelect]
  );

  return {
    handleCTVSelect,
    onCTVClick,
    handleBannerOrNativeSelect,
    onBannerOrNativeClick,
    handleVideoSelect,
    onVideoClick,
  };
};
